/**
 * EU AI Act Risk Classification Engine
 * Classifies AI systems according to EU AI Act risk categories
 */

import {
  AISystem,
  AISystemCharacteristics,
  RiskLevel,
  RiskClassificationResult,
  OrganizationContext,
  ComplianceRequirement,
  AssessmentResults,
  ComplianceGap,
  PrioritizedAction,
  ComplianceStatus,
  TimelineMilestone,
} from './eu-ai-act-types';
import { HIGH_RISK_REQUIREMENTS, LIMITED_RISK_REQUIREMENTS, GPAI_REQUIREMENTS } from './eu-ai-act-requirements';

// EU AI Act Timeline Milestones
export const EU_AI_ACT_MILESTONES: TimelineMilestone[] = [
  {
    date: '2024-08-01',
    title: 'AI Act Enters Into Force',
    description: 'The regulation officially enters into force',
    applicableCategories: ['unacceptable', 'high', 'limited', 'minimal'],
    isPassed: true,
  },
  {
    date: '2025-02-02',
    title: 'Prohibited AI Systems Ban',
    description: 'Unacceptable risk AI systems become prohibited',
    applicableCategories: ['unacceptable'],
    isPassed: true,
  },
  {
    date: '2025-08-02',
    title: 'GPAI Rules & Governance',
    description: 'General-purpose AI rules and governance provisions apply',
    applicableCategories: ['high', 'limited'],
    isPassed: false,
  },
  {
    date: '2026-08-02',
    title: 'Main Provisions Apply',
    description: 'Most AI Act provisions become applicable including high-risk requirements',
    applicableCategories: ['high', 'limited'],
    isPassed: false,
  },
  {
    date: '2027-08-02',
    title: 'Annex I High-Risk Systems',
    description: 'High-risk AI systems listed in Annex I (safety components) must comply',
    applicableCategories: ['high'],
    isPassed: false,
  },
];

/**
 * Check if an AI system has prohibited characteristics (Article 5)
 */
function checkProhibitedCharacteristics(chars: AISystemCharacteristics): string[] {
  const reasons: string[] = [];

  if (chars.socialScoring) {
    reasons.push('Social scoring by public authorities (Art. 5(1)(c))');
  }
  if (chars.realTimeBiometricPublic) {
    reasons.push('Real-time remote biometric identification in public spaces (Art. 5(1)(h))');
  }
  if (chars.emotionRecognitionWorkplace) {
    reasons.push('Emotion recognition in workplace or educational institutions (Art. 5(1)(f))');
  }
  if (chars.subliminalManipulation) {
    reasons.push('Subliminal techniques to materially distort behavior (Art. 5(1)(a))');
  }
  if (chars.exploitsVulnerabilities) {
    reasons.push('Exploiting vulnerabilities of specific groups (Art. 5(1)(b))');
  }
  if (chars.predictivePolicingIndividual) {
    reasons.push('Individual predictive policing based solely on profiling (Art. 5(1)(d))');
  }
  if (chars.facialRecognitionScraping) {
    reasons.push('Facial recognition databases through untargeted scraping (Art. 5(1)(e))');
  }

  return reasons;
}

/**
 * Check if an AI system qualifies as high-risk (Annex III)
 */
function checkHighRiskCharacteristics(chars: AISystemCharacteristics): string[] {
  const reasons: string[] = [];

  if (chars.biometricIdentification) {
    reasons.push('Biometric identification and categorization (Annex III, 1)');
  }
  if (chars.biometricCategorization) {
    reasons.push('Biometric categorization of natural persons (Annex III, 1)');
  }
  if (chars.criticalInfrastructure) {
    reasons.push('Safety component of critical infrastructure (Annex III, 2)');
  }
  if (chars.educationAssessment) {
    reasons.push('AI in education for assessment or admission (Annex III, 3)');
  }
  if (chars.employmentDecisions) {
    reasons.push('Employment, worker management, or recruitment decisions (Annex III, 4)');
  }
  if (chars.essentialServicesAccess) {
    reasons.push('Access to essential private/public services (Annex III, 5)');
  }
  if (chars.creditScoring) {
    reasons.push('Credit scoring and creditworthiness assessment (Annex III, 5(b))');
  }
  if (chars.emergencyServices) {
    reasons.push('Emergency services dispatch and prioritization (Annex III, 5(c))');
  }
  if (chars.lawEnforcementUse) {
    reasons.push('Law enforcement applications (Annex III, 6)');
  }
  if (chars.migrationAsylum) {
    reasons.push('Migration, asylum, and border control (Annex III, 7)');
  }
  if (chars.justiceAdministration) {
    reasons.push('Administration of justice and democratic processes (Annex III, 8)');
  }
  if (chars.democraticProcesses) {
    reasons.push('Influence on democratic processes (Annex III, 8)');
  }

  return reasons;
}

/**
 * Check if an AI system requires transparency obligations (Article 50)
 */
function checkLimitedRiskCharacteristics(chars: AISystemCharacteristics): string[] {
  const reasons: string[] = [];

  if (chars.interactsWithHumans) {
    reasons.push('System designed to interact with natural persons (Art. 50(1))');
  }
  if (chars.generatesContent) {
    reasons.push('Generates or manipulates content (Art. 50(4))');
  }
  if (chars.emotionRecognition && !chars.emotionRecognitionWorkplace) {
    reasons.push('Emotion recognition system outside prohibited contexts (Art. 50(3))');
  }
  if (chars.deepfakeGeneration) {
    reasons.push('Generates deep fake content (Art. 50(4))');
  }

  return reasons;
}

/**
 * Classify a single AI system's risk level
 */
export function classifyAISystem(system: AISystem): RiskClassificationResult {
  const chars = system.characteristics;

  // Check for prohibited (unacceptable risk) characteristics first
  const prohibitedReasons = checkProhibitedCharacteristics(chars);
  if (prohibitedReasons.length > 0) {
    return {
      system,
      riskLevel: 'unacceptable',
      reasons: prohibitedReasons,
      applicableArticles: ['Article 5 - Prohibited AI Practices'],
      requirements: [],
      deadline: '2025-02-02',
    };
  }

  // Check for high-risk characteristics
  const highRiskReasons = checkHighRiskCharacteristics(chars);
  if (highRiskReasons.length > 0) {
    return {
      system,
      riskLevel: 'high',
      reasons: highRiskReasons,
      applicableArticles: [
        'Article 9 - Risk Management System',
        'Article 10 - Data Governance',
        'Article 11 - Technical Documentation',
        'Article 12 - Record-Keeping',
        'Article 13 - Transparency',
        'Article 14 - Human Oversight',
        'Article 15 - Accuracy, Robustness, Cybersecurity',
        'Article 43 - Conformity Assessment',
      ],
      requirements: HIGH_RISK_REQUIREMENTS,
      deadline: '2026-08-02',
    };
  }

  // Check for limited risk (transparency) requirements
  const limitedRiskReasons = checkLimitedRiskCharacteristics(chars);
  if (limitedRiskReasons.length > 0) {
    return {
      system,
      riskLevel: 'limited',
      reasons: limitedRiskReasons,
      applicableArticles: ['Article 50 - Transparency Obligations'],
      requirements: LIMITED_RISK_REQUIREMENTS,
      deadline: '2026-08-02',
    };
  }

  // Default to minimal risk
  return {
    system,
    riskLevel: 'minimal',
    reasons: ['No specific risk indicators identified - voluntary compliance encouraged'],
    applicableArticles: [],
    requirements: [],
    deadline: 'N/A',
  };
}

/**
 * Determine if the EU AI Act applies to an organization
 */
export function determineApplicability(org: OrganizationContext): {
  applies: boolean;
  reasons: string[];
} {
  const reasons: string[] = [];
  let applies = false;

  if (org.location === 'eu') {
    applies = true;
    reasons.push('Organization is located in the EU');
  }

  if (org.location === 'eu-serving') {
    applies = true;
    reasons.push('Organization places AI systems on EU market or provides output to EU users');
  }

  if (org.developsSystems) {
    reasons.push('Organization develops AI systems');
  }

  if (org.deploysSystems) {
    reasons.push('Organization deploys AI systems');
  }

  if (org.importsDistributes) {
    reasons.push('Organization imports or distributes AI systems in EU');
  }

  return { applies, reasons };
}

/**
 * Analyze compliance gaps
 */
export function analyzeComplianceGaps(
  classifications: RiskClassificationResult[],
  currentStatus: ComplianceStatus
): ComplianceGap[] {
  const gaps: ComplianceGap[] = [];

  // Get all unique requirements from high-risk systems
  const highRiskSystems = classifications.filter((c) => c.riskLevel === 'high');
  if (highRiskSystems.length === 0) return gaps;

  const affectedSystemNames = highRiskSystems.map((c) => c.system.name);

  // Check each compliance area
  if (!currentStatus.hasRiskManagementSystem) {
    const req = HIGH_RISK_REQUIREMENTS.find((r) => r.category === 'risk-management');
    if (req) {
      gaps.push({
        requirement: req,
        currentStatus: 'missing',
        affectedSystems: affectedSystemNames,
        effortEstimate: 'high',
      });
    }
  }

  if (!currentStatus.hasDataGovernance) {
    const req = HIGH_RISK_REQUIREMENTS.find((r) => r.category === 'data-governance');
    if (req) {
      gaps.push({
        requirement: req,
        currentStatus: 'missing',
        affectedSystems: affectedSystemNames,
        effortEstimate: 'high',
      });
    }
  }

  if (!currentStatus.hasTechnicalDocumentation) {
    const req = HIGH_RISK_REQUIREMENTS.find((r) => r.category === 'technical-documentation');
    if (req) {
      gaps.push({
        requirement: req,
        currentStatus: 'missing',
        affectedSystems: affectedSystemNames,
        effortEstimate: 'medium',
      });
    }
  }

  if (!currentStatus.hasHumanOversight) {
    const req = HIGH_RISK_REQUIREMENTS.find((r) => r.category === 'human-oversight');
    if (req) {
      gaps.push({
        requirement: req,
        currentStatus: 'missing',
        affectedSystems: affectedSystemNames,
        effortEstimate: 'medium',
      });
    }
  }

  if (!currentStatus.hasTransparencyMeasures) {
    const req = HIGH_RISK_REQUIREMENTS.find((r) => r.category === 'transparency');
    if (req) {
      gaps.push({
        requirement: req,
        currentStatus: 'missing',
        affectedSystems: affectedSystemNames,
        effortEstimate: 'medium',
      });
    }
  }

  if (!currentStatus.hasConformityAssessment) {
    const req = HIGH_RISK_REQUIREMENTS.find((r) => r.category === 'conformity-assessment');
    if (req) {
      gaps.push({
        requirement: req,
        currentStatus: 'missing',
        affectedSystems: affectedSystemNames,
        effortEstimate: 'high',
      });
    }
  }

  return gaps;
}

/**
 * Generate prioritized action items
 */
export function generatePrioritizedActions(
  classifications: RiskClassificationResult[],
  gaps: ComplianceGap[]
): PrioritizedAction[] {
  const actions: PrioritizedAction[] = [];
  let priority = 1;

  // Urgent: Handle any prohibited systems
  const prohibitedSystems = classifications.filter((c) => c.riskLevel === 'unacceptable');
  if (prohibitedSystems.length > 0) {
    actions.push({
      id: 'action-prohibited',
      title: 'Immediately Discontinue Prohibited AI Systems',
      description: `You have ${prohibitedSystems.length} AI system(s) that fall under prohibited categories. These must be discontinued immediately as of February 2025.`,
      priority: priority++,
      deadline: '2025-02-02',
      relatedRequirements: ['Article 5'],
      affectedSystems: prohibitedSystems.map((s) => s.system.name),
      estimatedEffort: 'Immediate action required',
    });
  }

  // High priority: Address compliance gaps
  gaps.forEach((gap) => {
    actions.push({
      id: `action-gap-${gap.requirement.id}`,
      title: `Implement ${gap.requirement.title}`,
      description: gap.requirement.description,
      priority: priority++,
      deadline: '2026-08-02',
      relatedRequirements: [gap.requirement.article],
      affectedSystems: gap.affectedSystems,
      estimatedEffort: gap.effortEstimate === 'high' ? '3-6 months' : gap.effortEstimate === 'medium' ? '1-3 months' : '< 1 month',
    });
  });

  // Add conformity assessment if needed
  const highRiskSystems = classifications.filter((c) => c.riskLevel === 'high');
  if (highRiskSystems.length > 0 && !gaps.find((g) => g.requirement.category === 'conformity-assessment')) {
    actions.push({
      id: 'action-conformity',
      title: 'Complete Conformity Assessment',
      description: 'Conduct conformity assessment for high-risk AI systems and affix CE marking',
      priority: priority++,
      deadline: '2026-08-02',
      relatedRequirements: ['Article 43'],
      affectedSystems: highRiskSystems.map((s) => s.system.name),
      estimatedEffort: '2-4 months',
    });
  }

  // Add EU database registration
  if (highRiskSystems.length > 0) {
    actions.push({
      id: 'action-registration',
      title: 'Register in EU Database',
      description: 'Register high-risk AI systems in the EU database before placing on market',
      priority: priority++,
      deadline: '2026-08-02',
      relatedRequirements: ['Article 71'],
      affectedSystems: highRiskSystems.map((s) => s.system.name),
      estimatedEffort: '1-2 weeks per system',
    });
  }

  return actions;
}

/**
 * Calculate overall compliance score
 */
export function calculateComplianceScore(
  classifications: RiskClassificationResult[],
  currentStatus: ComplianceStatus
): number {
  const highRiskCount = classifications.filter((c) => c.riskLevel === 'high').length;

  if (highRiskCount === 0) {
    // No high-risk systems, good standing
    return 100;
  }

  // Score based on compliance measures in place
  let score = 0;
  const totalMeasures = 9;

  if (currentStatus.hasAIGovernancePolicy) score += 1;
  if (currentStatus.hasRiskManagementSystem) score += 1.5;
  if (currentStatus.hasDataGovernance) score += 1.5;
  if (currentStatus.hasTechnicalDocumentation) score += 1;
  if (currentStatus.hasHumanOversight) score += 1;
  if (currentStatus.hasTransparencyMeasures) score += 1;
  if (currentStatus.hasQualityManagement) score += 0.5;
  if (currentStatus.hasIncidentReporting) score += 0.5;
  if (currentStatus.hasConformityAssessment) score += 1;

  return Math.round((score / totalMeasures) * 100);
}

/**
 * Generate complete assessment results
 */
export function generateAssessmentResults(
  org: OrganizationContext,
  systems: AISystem[],
  currentStatus: ComplianceStatus
): AssessmentResults {
  const applicability = determineApplicability(org);
  const classifications = systems.map((s) => classifyAISystem(s));
  const gaps = analyzeComplianceGaps(classifications, currentStatus);
  const actions = generatePrioritizedActions(classifications, gaps);
  const complianceScore = calculateComplianceScore(classifications, currentStatus);

  // Find next applicable deadline
  const now = new Date();
  const nextDeadline = EU_AI_ACT_MILESTONES.find(
    (m) => new Date(m.date) > now
  )?.date || 'N/A';

  return {
    overallApplicability: applicability.applies,
    systemClassifications: classifications,
    complianceGaps: gaps,
    prioritizedActions: actions,
    nextDeadline,
    complianceScore,
  };
}

/**
 * Get risk level color for UI
 */
export function getRiskLevelColor(level: RiskLevel): string {
  switch (level) {
    case 'unacceptable':
      return 'red';
    case 'high':
      return 'orange';
    case 'limited':
      return 'yellow';
    case 'minimal':
      return 'green';
    default:
      return 'gray';
  }
}

/**
 * Get risk level label
 */
export function getRiskLevelLabel(level: RiskLevel): string {
  switch (level) {
    case 'unacceptable':
      return 'Prohibited';
    case 'high':
      return 'High Risk';
    case 'limited':
      return 'Limited Risk';
    case 'minimal':
      return 'Minimal Risk';
    default:
      return 'Unknown';
  }
}
